/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient, ApiError } from '../lib/api-client';
import { FrontendLogger } from '../lib/logger';
import { AnalyticsLogger } from '../lib/analytics';

interface User {
  id: number;
  username: string;
  role: 'ADMIN' | 'MANAGER' | 'STREAMER' | 'VIEWER' | 'USER';
}

interface LoginResult {
  user: User;
  must_change_password: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<LoginResult>;
  // Email + password authentication methods
  viewerEmailLogin: (email: string, password: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<boolean>;
  resendVerification: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  logout: () => void;
  isAdmin: () => boolean;
  isManager: () => boolean;
  isAdminOrManager: () => boolean;
  isStreamer: () => boolean;
  isViewer: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // ログシステム統合: ユーザー情報をLoggerと同期
  useEffect(() => {
    const logger = FrontendLogger.getInstance();
    if (user) {
      logger.setUser(user.id, user.role);
    } else {
      logger.clearUser();
    }
  }, [user]);

  // Initialize - check if user is already authenticated
  useEffect(() => {
    const initAuth = async () => {
      try {
        const hasAccessToken = apiClient.isAuthenticated();
        const hasRestoredSession = hasAccessToken || await apiClient.refresh();

        if (hasRestoredSession) {
          const userData = await apiClient.verify();
          setUser({
            id: parseInt(userData.user_id),
            username: userData.username,
            role: userData.role as 'ADMIN' | 'MANAGER' | 'STREAMER' | 'VIEWER' | 'USER',
          });
        }
      } catch (error) {
        // Token invalid, clear it
        apiClient.logout();
        setUser(null);
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (username: string, password: string): Promise<LoginResult> => {
    try {
      const loginResponse = await apiClient.login(username, password);
      const userData = await apiClient.verify();
      const user: User = {
        id: parseInt(userData.user_id),
        username: userData.username,
        role: userData.role as 'ADMIN' | 'MANAGER' | 'STREAMER' | 'VIEWER' | 'USER',
      };
      // 先にloggerにユーザー情報を設定（useEffectより先に実行するため）
      FrontendLogger.getInstance().setUser(user.id, user.role);
      setUser(user);
      // 分析イベント: ログイン成功
      AnalyticsLogger.getInstance().trackLogin(user.id, 'password');
      return {
        user,
        must_change_password: loginResponse.must_change_password ?? false,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(error.detail);
      }
      throw error;
    }
  };

  // Email + password authentication methods

  const viewerEmailLogin = async (email: string, password: string) => {
    try {
      const response = await apiClient.viewerEmailLogin(email, password);
      const newUser = {
        id: response.user.id,
        username: response.user.username,
        role: response.user.role as 'ADMIN' | 'MANAGER' | 'STREAMER' | 'VIEWER' | 'USER',
      };
      // 先にloggerにユーザー情報を設定（useEffectより先に実行するため）
      FrontendLogger.getInstance().setUser(newUser.id, newUser.role);
      setUser(newUser);
      // 分析イベント: メールログイン成功
      AnalyticsLogger.getInstance().trackLogin(newUser.id, 'email');
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(error.detail);
      }
      throw error;
    }
  };

  const verifyEmail = async (token: string) => {
    try {
      const response = await apiClient.verifyEmail(token);
      // 確認成功時はユーザー情報をセット
      if (response.verified && response.user) {
        const newUser = {
          id: response.user.id,
          username: response.user.username,
          role: response.user.role as 'ADMIN' | 'MANAGER' | 'STREAMER' | 'VIEWER' | 'USER',
        };
        // 先にloggerにユーザー情報を設定（useEffectより先に実行するため）
        FrontendLogger.getInstance().setUser(newUser.id, newUser.role);
        setUser(newUser);
      }
      return response.verified;
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(error.detail);
      }
      throw error;
    }
  };

  const resendVerification = async (email: string) => {
    try {
      await apiClient.resendVerification(email);
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(error.detail);
      }
      throw error;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await apiClient.forgotPassword(email);
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(error.detail);
      }
      throw error;
    }
  };

  const resetPassword = async (token: string, newPassword: string) => {
    try {
      await apiClient.resetPassword(token, newPassword);
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(error.detail);
      }
      throw error;
    }
  };

  const logout = () => {
    // 分析イベント: ログアウト（ユーザー情報がクリアされる前に記録）
    AnalyticsLogger.getInstance().trackLogout();
    apiClient.logout();
    setUser(null);
  };

  const isAdmin = () => user?.role === 'ADMIN';
  const isManager = () => user?.role === 'MANAGER';
  const isAdminOrManager = () => user?.role === 'ADMIN' || user?.role === 'MANAGER';
  const isStreamer = () => user?.role === 'STREAMER';
  const isViewer = () => user?.role === 'VIEWER' || user?.role === 'USER';

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    login,
    viewerEmailLogin,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
    logout,
    isAdmin,
    isManager,
    isAdminOrManager,
    isStreamer,
    isViewer,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
